import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import AdministradorPagosModal from "../../components/AdministradorPagosModal";
import { formatDate, parseToISO, getTodayFormatted } from "../../utils/dateUtils";
import "./ContratosList.css";

const API_BASE = `${process.env.REACT_APP_API_URL || ""}`;

export default function ContratosList() {
  const navigate = useNavigate();
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("TODOS"); // TODOS, VIGENTES, FINALIZADOS
  const [busqueda, setBusqueda] = useState("");

  const [pagosModalOpen, setPagosModalOpen] = useState(false);
  const [selectedContratoId, setSelectedContratoId] = useState(null);

  const token = localStorage.getItem("token");
  const authConfig = {
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
  };

  const cargarContratos = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const res = await axios.get(`${API_BASE}/api/contratoarriendo/`, authConfig);
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setContratos(data);
    } catch (err) {
      console.error("Error al cargar contratos:", err);
      setErrorMsg("No fue posible cargar los contratos de arrendamiento.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    cargarContratos();
  }, [cargarContratos]);

  const handleOpenPagos = (id) => {
    setSelectedContratoId(id);
    setPagosModalOpen(true);
  };

  const handleFinalizarContrato = async (contratoId) => {
    const hoy = getTodayFormatted();
    const fecha = window.prompt(
      "Ingrese la Fecha de Entrega del Inmueble para finalizar el contrato (dd/mm/aaaa):",
      hoy
    );
    if (!fecha || !fecha.trim()) return;

    const fechaParsed = parseToISO(fecha.trim());
    try {
      await axios.patch(
        `${API_BASE}/api/contratoarriendo/${contratoId}/`,
        { TCAFechaEntregaInmueble: fechaParsed },
        authConfig
      );
      alert("Contrato finalizado exitosamente. Ya se encuentra disponible para registrar reportes en el módulo Reportar.");
      await cargarContratos();
    } catch (err) {
      console.error("Error al finalizar contrato:", err);
      const msg =
        err.response?.data?.TCAFechaEntregaInmueble?.[0] ||
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        "No fue posible finalizar el contrato.";
      alert(msg);
    }
  };

  const formatCOP = (val) => {
    if (val === undefined || val === null) return "$0";
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const contratosFiltrados = contratos.filter((c) => {
    const vigente = c.TCAFechaEntregaInmueble === null;
    if (filtroEstado === "VIGENTES" && !vigente) return false;
    if (filtroEstado === "FINALIZADOS" && vigente) return false;

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      const matchDir = c.TBDireccion?.toLowerCase().includes(q);
      const matchMat = c.TBNoMatricula?.toLowerCase().includes(q);
      const matchId = c.TCAIDContrato?.toString().includes(q);
      return matchDir || matchMat || matchId;
    }
    return true;
  });

  const totalVigentes = contratos.filter((c) => c.TCAFechaEntregaInmueble === null).length;
  const totalFinalizados = contratos.length - totalVigentes;

  return (
    <AppShell
      eyebrow="Módulo de Contratos"
      title="Contratos de Arrendamiento"
      subtitle="Consulta contratos vigentes, gestiona pagos de canon con incremento de IPC año a año y administra tus inmuebles."
      heroActions={
        <button
          type="button"
          className="app-button app-button--primary"
          onClick={() => navigate("/contratos/nuevo")}
        >
          + Crear Nuevo Contrato
        </button>
      }
    >
      {/* Tarjetas resumen */}
      <section className="app-stat-grid">
        <div
          className="app-stat"
          onClick={() => setFiltroEstado("TODOS")}
          style={{
            cursor: "pointer",
            borderColor: filtroEstado === "TODOS" ? "var(--app-primary, #2563eb)" : "inherit",
          }}
        >
          <strong>{contratos.length}</strong>
          <span>Total Contratos</span>
        </div>
        <div
          className="app-stat"
          onClick={() => setFiltroEstado("VIGENTES")}
          style={{
            cursor: "pointer",
            borderColor: filtroEstado === "VIGENTES" ? "#16a34a" : "inherit",
          }}
        >
          <strong style={{ color: "#16a34a" }}>{totalVigentes}</strong>
          <span>Contratos Vigentes</span>
        </div>
        <div
          className="app-stat"
          onClick={() => setFiltroEstado("FINALIZADOS")}
          style={{
            cursor: "pointer",
            borderColor: filtroEstado === "FINALIZADOS" ? "#64748b" : "inherit",
          }}
        >
          <strong style={{ color: "#64748b" }}>{totalFinalizados}</strong>
          <span>Finalizados</span>
        </div>
      </section>

      {/* Contenido principal */}
      <section className="app-surface">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Listado de Contratos</h2>
            <p style={{ margin: "4px 0 0", color: "var(--app-text-muted)", fontSize: "0.9rem" }}>
              Selecciona un contrato vigente para registrar pagos o consultar su estado de cuenta.
            </p>
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="Buscar por dirección o matrícula..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="app-input"
              style={{ width: "260px" }}
            />
          </div>
        </div>

        {loading ? (
          <div className="contratos-loading">Cargando contratos...</div>
        ) : errorMsg ? (
          <div className="contratos-error">{errorMsg}</div>
        ) : contratosFiltrados.length === 0 ? (
          <div className="contratos-empty">
            <p>No se encontraron contratos con los filtros aplicados.</p>
            <button
              type="button"
              className="app-button app-button--secondary"
              onClick={() => navigate("/contratos/nuevo")}
            >
              Crear Nuevo Contrato
            </button>
          </div>
        ) : (
          <div className="contratos-grid">
            {contratosFiltrados.map((c) => {
              const vigente = c.TCAFechaEntregaInmueble === null;
              const ec = c.estado_cuenta;

              return (
                <div
                  key={c.TCAIDContrato}
                  className={`contrato-card ${vigente ? "contrato-card--vigente" : "contrato-card--finalizado"}`}
                >
                  <div className="contrato-card-header">
                    <div>
                      <span className="contrato-card-id">Contrato #{c.TCAIDContrato}</span>
                      <h3 className="contrato-card-title">{c.TBDireccion}</h3>
                    </div>
                    <span
                      className={`contrato-status-badge ${
                        vigente ? "contrato-status-badge--vigente" : "contrato-status-badge--finalizado"
                      }`}
                    >
                      {vigente ? "Vigente" : "Finalizado"}
                    </span>
                  </div>

                  <div className="contrato-card-body">
                    <div className="contrato-field">
                      <span className="contrato-field-label">Matrícula Inmobiliaria:</span>
                      <span className="contrato-field-val">{c.TBNoMatricula}</span>
                    </div>

                    <div className="contrato-field">
                      <span className="contrato-field-label">Fecha de Inicio:</span>
                      <span className="contrato-field-val">{formatDate(c.TCAFechaInicioContrato)}</span>
                    </div>

                    {c.TCAFechaEntregaInmueble && (
                      <div className="contrato-field">
                        <span className="contrato-field-label">Fecha de Entrega:</span>
                        <span className="contrato-field-val" style={{ color: "#dc2626" }}>
                          {formatDate(c.TCAFechaEntregaInmueble)}
                        </span>
                      </div>
                    )}

                    <div className="contrato-field">
                      <span className="contrato-field-label">Canon Inicial:</span>
                      <span className="contrato-field-val">{formatCOP(c.TCAValorCanonContrato)}</span>
                    </div>

                    {ec && (
                      <div className="contrato-field">
                        <span className="contrato-field-label">Canon Actual (con IPC):</span>
                        <span className="contrato-field-val" style={{ color: "#0284c7", fontWeight: "700" }}>
                          {formatCOP(ec.canon_actual)}
                        </span>
                      </div>
                    )}

                    {/* Estado de Pagos */}
                    {ec ? (
                      <div
                        className={`contrato-payment-status ${
                          ec.esta_al_dia
                            ? "contrato-payment-status--aldia"
                            : "contrato-payment-status--pendiente"
                        }`}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <strong>
                            {ec.esta_al_dia
                              ? "✓ Al día (Saldo: $0)"
                              : `⚠️ Saldo Pendiente: ${formatCOP(ec.saldo)}`}
                          </strong>
                          {ec.meses_transcurridos > 0 && (
                            <small>{ec.meses_transcurridos} mes(es) transcurridos</small>
                          )}
                        </div>
                        {!ec.esta_al_dia && (
                          <div style={{ fontSize: "0.8rem", marginTop: "3px", opacity: 0.9 }}>
                            Aprox. {ec.meses_pendientes} mes(es) pendientes por pagar
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="contrato-payment-status" style={{ background: "#f8fafc", color: "#64748b" }}>
                        Cálculo de saldo disponible en el administrador de pagos.
                      </div>
                    )}

                    {/* Personas asociadas */}
                    {c.personas && c.personas.length > 0 && (
                      <div className="contrato-personas-list">
                        <span className="contrato-personas-title">Partes del Contrato:</span>
                        <ul>
                          {c.personas.map((p, idx) => {
                            const nombre = p.nombre_completo || [p.nombres, p.apellidos].filter(Boolean).join(" ");
                            return (
                              <li key={idx}>
                                <strong>{p.TCARTipoParticipacion}:</strong>{" "}
                                {nombre ? (
                                  <>
                                    <span>{nombre}</span>{" "}
                                    <span style={{ color: "#64748b", fontSize: "0.85rem" }}>
                                      (Doc. {p.TPNoDocumento})
                                    </span>
                                  </>
                                ) : (
                                  `Doc. ${p.TPNoDocumento}`
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="contrato-card-actions" style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="contrato-btn-pagos"
                      onClick={() => handleOpenPagos(c.TCAIDContrato)}
                    >
                      💳 Pagos de Canon
                    </button>
                    {vigente && (
                      <button
                        type="button"
                        onClick={() => handleFinalizarContrato(c.TCAIDContrato)}
                        title="Registrar fecha de entrega del inmueble para finalizar el contrato"
                        style={{
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          color: "#b91c1c",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          fontWeight: "600",
                          cursor: "pointer"
                        }}
                      >
                        🚪 Finalizar Contrato
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Modal de Pagos */}
      <AdministradorPagosModal
        isOpen={pagosModalOpen}
        onClose={() => setPagosModalOpen(false)}
        contratoId={selectedContratoId}
        onUpdated={cargarContratos}
      />
    </AppShell>
  );
}
